/**
 * A review of what needs attention across the system, and the tasks it
 * suggests.
 *
 * Four readings, each the platform's own: open alerts by severity (and whether
 * anything has been raised from them), open work orders at P1–P2, work orders
 * past their due date or their priority's allowance, and certificates,
 * documents and services coming due. Each finding says which record put it on
 * the list and what could be done, with what doing it costs.
 *
 * The suggested task is phrased as an option, never a decision: "raise a work
 * order against it" is offered because the platform records that nothing has
 * been raised, not because she judged it should be.
 *
 * Where a model is connected (OpenAI, Claude, Gemini or a local one), it may
 * put the findings in order of what to look at first and say why in a line.
 * It sees only the findings, it may only name findings by their id, and every
 * figure in what it writes is checked against the findings; a line that fails
 * is dropped and the platform's own order stands. With no model, the order is
 * severity then priority — the record's, not hers.
 */
import type { DataSource, Principal } from "../api/dataSource";
import type { Alert } from "../domain/alerts";
import { verifyFigures } from "../domain/decision";
import { isOpen, isOverdue, priorityLabel, type WorkOrder } from "../domain/workOrders";
import { formatDate } from "../lib/format";
import { canSee, type AccessLevel } from "./access";
import { scanDue } from "./due";
import { askModel, hasModel, type ExternalConfig } from "./external";

export type Weight = "critical" | "high" | "watch";

export interface Finding {
  id: string;
  weight: Weight;
  /** 0 is worst. The platform's severity and priority, never a score of hers. */
  rank: number;
  label: string;
  because: string;
  href: string;
  /** Words to find it by on its screen, so the walkthrough can point at the row. */
  find?: string;
  /** The suggested task, as an option. */
  task: string;
  tradeOff: string;
}

export interface Review {
  findings: Finding[];
  counts: Record<Weight, number>;
  /** When a model ordered them, what it said and which model; null when the platform's order stands. */
  ordered: { by: string; why: Record<string, string> } | null;
  checked: string[];
}

const day = (iso?: string) => (iso ? formatDate(iso) : "unknown");

function daysSince(iso: string | undefined, now: number): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Math.floor((now - t) / 86_400_000) : null;
}

export async function review(
  source: DataSource,
  siteId: string,
  principal: Principal,
  level: AccessLevel,
  now: number = Date.now(),
): Promise<Review> {
  const [wos, alerts, due] = await Promise.all([
    source.listWorkOrders({ siteId }).catch(() => ({ items: [] as WorkOrder[], truncated: false })),
    source.listAlerts(siteId).catch(() => [] as Alert[]),
    scanDue(source, siteId, principal, now).catch(() => []),
  ]);
  const out: Finding[] = [];
  const checked: string[] = [];

  const openAlerts = alerts.filter((a) => a.status === "open");
  checked.push(`${openAlerts.length} open alerts`);
  for (const a of openAlerts) {
    const raised = wos.items.find((w) => w.alertId === a.id);
    if (a.severity < 3 && raised) continue;
    const weight: Weight = a.severity >= 4 && !raised ? "critical" : a.severity >= 4 ? "high" : raised ? "watch" : "high";
    out.push({
      id: `alert:${a.id}`,
      weight,
      rank: (weight === "critical" ? 0 : weight === "high" ? 20 : 40) + (5 - a.severity),
      label: a.title,
      because: `Alert · severity ${a.severity}${a.zoneCode ? ` · ${a.zoneCode}` : ""} · raised ${day(a.raisedAt)}${raised ? ` · ${raised.number} raised from it (${raised.status})` : " · nothing raised from it"}`,
      href: "/alerts",
      find: a.title,
      task: raised ? `Follow ${raised.number}, raised from this alert` : "Create a new work order against it, or acknowledge it if the rule is noisy",
      tradeOff: raised
        ? "Watching costs nothing and does not speed it up."
        : "A work order commits a crew you may want elsewhere today; acknowledging puts the delay on the record.",
    });
  }

  const open = wos.items.filter((w) => isOpen(w.status));
  checked.push(`${open.length} open work orders`);
  for (const w of open) {
    const overdue = isOverdue(w, new Date(now));
    if (w.priority > 2 && !overdue) continue;
    const age = daysSince(w.openedAt, now);
    const weight: Weight = w.priority === 1 || (overdue && w.priority <= 2) ? "critical" : overdue || w.priority === 2 ? "high" : "watch";
    const held = w.status === "on_hold";
    out.push({
      id: `wo:${w.id}`,
      weight,
      rank: (weight === "critical" ? 5 : weight === "high" ? 25 : 45) + w.priority,
      label: `${w.number} · ${w.title}`,
      because:
        `${priorityLabel(w.priority)} · ${w.status.replace(/_/g, " ")}${w.zoneCode ? ` · ${w.zoneCode}` : ""}` +
        (overdue ? (w.dueAt ? ` · past due ${day(w.dueAt)}` : ` · open ${age ?? "?"} days, past its priority's allowance`) : ` · opened ${day(w.openedAt)}`),
      href: `/work-orders/${w.id}`,
      find: w.number,
      task: held
        ? `Check what ${w.number} is held for, and take it off hold if that has cleared`
        : w.status === "draft" || w.status === "approved"
          ? `Dispatch ${w.number}`
          : `Read ${w.number}'s history and chase the crew`,
      tradeOff: held
        ? "Only worth doing if the reason has actually cleared."
        : w.status === "draft" || w.status === "approved"
          ? "Starts the clock and takes a crew off whatever they are on."
          : "Costs a call; the record shows whether it was needed.",
    });
  }

  checked.push(`${due.length} certificates, documents and services coming due`);
  for (const d of due) {
    if (d.days > 15) continue;
    /* a service due is a work order, and the work orders above already carry it — once, with a work order's task */
    if (d.kind === "service" && out.some((f) => f.href === d.href)) continue;
    const weight: Weight = d.days < 0 ? "critical" : d.days <= 7 ? "high" : "watch";
    out.push({
      id: `due:${d.id}`,
      weight,
      rank: (weight === "critical" ? 3 : weight === "high" ? 23 : 43) + Math.max(0, Math.min(15, d.days)) / 10,
      label: d.label,
      because: d.detail,
      href: d.href ?? "/documents",
      /* the label's first part — a reference like "Cal.Cert.SFT933" — is what the page shows on its own */
      find: d.label.split(" · ")[0],
      task:
        d.kind === "service"
          ? `Get ${d.label.split(" · ")[0]} done or re-dated`
          : d.kind === "review"
            ? `Hold the review of ${d.label.split(" · ")[0]}${d.days < 0 ? " — it is overdue" : ""}`
            : d.days < 0
              ? `Renew or withdraw ${d.label.split(" · ")[0]} — it has lapsed`
              : `Start the renewal of ${d.label.split(" · ")[0]}`,
      tradeOff:
        d.kind === "service"
          ? "Re-dating is honest when the date was wrong; it is also how a service quietly never happens."
          : "It takes someone's time this week; a lapse is on the record from the day it happens.",
    });
  }

  const findings = out.filter((f) => canSee(level, f.href)).sort((a, b) => a.rank - b.rank);
  const counts = { critical: 0, high: 0, watch: 0 } as Record<Weight, number>;
  findings.forEach((f) => counts[f.weight]++);
  return { findings, counts, ordered: null, checked };
}

/* ── a model's order, checked ─────────────────────────────────────────── */

const ORDER_PROMPT = [
  "You help an airfield operations manager decide what to look at first.",
  "You are given findings from the platform's records, each with an id. You may only use those findings.",
  'Reply with JSON only: {"order":[{"id":"<finding id>","why":"<one short sentence>"}]} listing at most 6 findings, most pressing first.',
  "Do not state any number that is not in the findings. Do not decide anything; say why it deserves attention first.",
].join(" ");

/**
 * Ask the connected model to order the findings. Returns the review unchanged
 * when there is no model, it fails, or its answer does not survive checking.
 */
export async function orderWithModel(r: Review, cfg: ExternalConfig, signal?: AbortSignal): Promise<Review> {
  if (!hasModel(cfg) || r.findings.length < 2) return r;
  const pool = r.findings.slice(0, 15);
  const material = pool.map((f) => `${f.id} | ${f.weight} | ${f.label} | ${f.because}`).join("\n");
  const figures = new Set<string>((material.match(/\d[\d,]*(?:\.\d+)?/g) ?? []).map((x) => x.replace(/,/g, "")));
  try {
    const reply = await askModel(cfg, ORDER_PROMPT, material, signal);
    const json = reply.text.match(/\{[\s\S]*\}/)?.[0];
    if (!json) return r;
    const parsed = JSON.parse(json) as { order?: { id?: unknown; why?: unknown }[] };
    const byId = new Map(pool.map((f) => [f.id, f]));
    const why: Record<string, string> = {};
    const first: Finding[] = [];
    for (const o of parsed.order ?? []) {
      if (typeof o.id !== "string" || !byId.has(o.id) || why[o.id] !== undefined) continue;
      const line = typeof o.why === "string" ? o.why.trim().slice(0, 220) : "";
      /* a sentence carrying a figure the findings do not is dropped, and so is its place in the order */
      if (!line || verifyFigures(line, figures).length > 0) continue;
      why[o.id] = line;
      first.push(byId.get(o.id)!);
    }
    if (first.length === 0) return r;
    const rest = r.findings.filter((f) => why[f.id] === undefined);
    return { ...r, findings: [...first, ...rest], ordered: { by: reply.model, why } };
  } catch {
    return r;
  }
}

export function reviewLines(r: Review, siteCode: string): string[] {
  if (r.findings.length === 0) {
    return [`${siteCode}: nothing critical across alerts, work orders or what is coming due. Checked ${r.checked.join(", ")}.`];
  }
  const { critical, high, watch } = r.counts;
  return [
    `${siteCode}: ${critical} critical, ${high} high, ${watch} to watch — across alerts, work orders and what is coming due.`,
    r.ordered
      ? `Ordered by ${r.ordered.by}, checked against the records; each line below says why it is first.`
      : "Worst first, by the platform's own severity, priority and dates. What to do about each is yours.",
  ];
}
